import React from 'react';
import styled from 'styled-components';
import { getHotelText } from '../config/hotelTexts';
import { useLanguage } from '../context/LanguageContext';

// Placeholder images - à remplacer par les vraies photos plus tard
const regionImages = {
  'mont-saint-michel': '/images/mont-customerly-DKKkMfzQE3o-unsplash.jpg',
  'plages-debarquement': '/images/beach-derick-mckinney-Lx9DEJ1jLuw-unsplash.jpg',
  'atelier-cuivre': '/images/copper-matt-seymour-UbsDLjqZ9RM-unsplash.jpg',
  'fonderie': '/images/cloche-luis-felipe-lins-ICccs4u7-yM-unsplash.jpg',
};

const PageHeader = styled.div`
  height: 40vh;
  min-height: 300px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/images/alentours-axp-photography-rLS2zPFLc_Q-unsplash.jpg');
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  margin-top: 70px; /* Pour laisser de l'espace pour le header fixe */
  background-color: transparent;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  color: ${({ theme }) => theme.colors.white};
`;

const RegionSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const RegionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const IntroText = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
`;

const IntroTitle = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 2rem;
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const IntroDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textLight};
`;

const AttractionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.xxl};
  margin-top: ${({ theme }) => theme.spacing.xxl};
`;

const AttractionCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  overflow: hidden;
  
  &.even {
    direction: rtl;
    
    > * {
      direction: ltr;
    }
  }
  
  &:nth-child(even) {
    direction: rtl;
    
    > * {
      direction: ltr;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    
    &:nth-child(even), &.even {
      direction: ltr;
    }
  }
`;

const AttractionImage = styled.div`
  height: 100%;
  min-height: 300px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const AttractionContent = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AttractionTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.8rem;
`;

const AttractionDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.6;
  font-size: 1.1rem;
`;

const AttractionDistance = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: 1.1rem;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
  }
`;

const AdditionalAttractions = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxl};
  background-color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
`;

const AdditionalTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  text-align: center;
`;

const AttractionsList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const AttractionItem = styled.li`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  box-shadow: ${({ theme }) => theme.shadows.small};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.accent};
    flex-shrink: 0;
  }
`;

const Region = () => {
  const { t } = useLanguage();
  
  const { language } = useLanguage();
  
  // Attractions supplémentaires
  const additionalAttractionsData = {
    fr: [
      'Granville et ses plages',
      'Coutances et sa cathédrale',
      'Saint-Lô',
      'Abbaye de Hambye',
      'Parc naturel régional des Marais du Cotentin',
      'Bayeux et sa tapisserie',
      'Cherbourg',
      'Caen et son mémorial',
      'Avranches',
    ],
    en: [
      'Granville and its beaches',
      'Coutances and its cathedral',
      'Saint-Lô',
      'Hambye Abbey',
      'Regional Natural Park of the Cotentin Marshes',
      'Bayeux and its tapestry',
      'Cherbourg',
      'Caen and its memorial',
      'Avranches',
    ]
  };
  
  const additionalAttractions = additionalAttractionsData[language] || additionalAttractionsData.fr;
  
  return (
    <>
      <PageHeader>
        <PageTitle>{t('region.pageTitle')}</PageTitle>
      </PageHeader>
      
      <RegionSection>
        <RegionContainer>
          <IntroText>
            <IntroTitle>{getHotelText(language, 'region.title')}</IntroTitle>
            <IntroDescription>
              {getHotelText(language, 'region.description')}
            </IntroDescription>
          </IntroText>
          
          <AttractionsGrid>
            {getHotelText(language, 'region.attractions').map((attraction, index) => (
              <a href={attraction.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }} key={index} className={index % 2 === 1 ? 'even' : ''}>
                <AttractionCard className={index % 2 === 1 ? 'even' : ''}>
                  <AttractionImage 
                    image={regionImages[attraction.image.split('.')[0]]} 
                  />
                  <AttractionContent>
                    <AttractionTitle>{attraction.name}</AttractionTitle>
                    <AttractionDescription>{attraction.description}</AttractionDescription>
                    <AttractionDistance>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                      </svg>
                      {t('region.distanceFrom', { distance: attraction.distance })}
                    </AttractionDistance>
                  </AttractionContent>
                </AttractionCard>
              </a>
            ))}
          </AttractionsGrid>
          
          <AdditionalAttractions>
            <AdditionalTitle>{t('region.otherPlaces')}</AdditionalTitle>
            <AttractionsList>
              {additionalAttractions.map((attraction, index) => (
                <AttractionItem key={index}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 16l-6.18-6.496A5.5 5.5 0 0 1 8 .5a5.5 5.5 0 0 1 6.18 9.004L8 16zm0-14A4.5 4.5 0 0 0 5.06 9.223l2.94 3.1 2.94-3.1A4.5 4.5 0 0 0 8 2z"/>
                  </svg>
                  {attraction}
                </AttractionItem>
              ))}
            </AttractionsList>
          </AdditionalAttractions>
          
          <div className="text-center mt-4">
            <p>{t('region.advice1')}</p>
            <p>{t('region.advice2')}</p>
          </div>
        </RegionContainer>
      </RegionSection>
    </>
  );
};

export default Region;
